import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { NotificationService } from './notification.service';

@Injectable({
  providedIn: 'root'
})
export class ErrorHandlerService {

  constructor(private notificationService: NotificationService) {}

  handleError(error: any): void {
    if (error instanceof HttpErrorResponse) {
      this.handleHttpError(error);
    } else if (error instanceof Error) {
      this.handleClientError(error);
    } else {
      this.handleUnknownError(error);
    }
  }

  private handleHttpError(error: HttpErrorResponse): void {
    let message: string;

    switch (error.status) {
      case 0:
        // Network error or CORS issue
        message = 'Unable to connect to the server. Please check your internet connection.';
        break;

      case 400:
        // Bad Request - Show backend error message
        message = this.extractErrorMessage(error);
        break;

      case 401:
        // Unauthorized
        message = 'Your session has expired. Please log in again.';
        // Optionally redirect to login
        break;

      case 403:
        // Forbidden
        message = 'You do not have permission to perform this action.';
        break;

      case 404:
        // Not Found
        message = 'The requested resource was not found.';
        break;

      case 409:
        // Conflict
        message = this.extractErrorMessage(error) || 'A conflict occurred. Please try again.';
        break;

      case 422:
        // Unprocessable Entity - Validation errors
        message = this.extractErrorMessage(error);
        break;

      case 500:
      case 502:
      case 503:
      case 504:
        // Server errors
        message = 'Something went wrong on our end. Please try again later.';
        break;

      default:
        message = 'An unexpected error occurred. Please try again.';
    }

    this.notificationService.showError(message);
  }

  private handleClientError(error: Error): void {
    console.error('Client Error:', error);
    this.notificationService.showError('An unexpected error occurred. Please refresh the page.');
  }

  private handleUnknownError(error: any): void {
    console.error('Unknown Error:', error);
    this.notificationService.showError('An unexpected error occurred. Please try again.');
  }

  private extractErrorMessage(error: HttpErrorResponse): string {
    // Try to extract message from different possible response structures
    if (error.error) {
      // Check for ApiResponse structure
      if (error.error.message) {
        return error.error.message;
      }

      // Check for validation errors
      if (error.error.errors) {
        if (Array.isArray(error.error.errors)) {
          return error.error.errors.join(', ');
        }
        if (typeof error.error.errors === 'object') {
          const messages = Object.values(error.error.errors).flat();
          return messages.join(', ');
        }
      }

      // Check for direct error message
      if (typeof error.error === 'string') {
        return error.error;
      }

      // Check for data.message
      if (error.error.data?.message) {
        return error.error.data.message;
      }
    }

    // Fallback to status text
    return error.statusText || 'An error occurred';
  }
}
